import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Box,
  Text,
  Grid,
  Heading,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Checkbox,
  useColorModeValue,
  Tag,
  TagLabel,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  MenuDivider,
} from "@chakra-ui/react";
import {
  CollecticonChevronDownSmall,
  CollecticonChevronUpSmall,
  CollecticonMap,
  CollecticonArrowSemiSpinCcw,
  CollecticonSwapVertical
} from "@devseed-ui/collecticons-chakra";
import { MapRef, MapLayerMouseEvent } from "react-map-gl";
import { BBox, Feature, Geometry } from "geojson";
import { bbox as turfBbox } from "@turf/bbox";
import { useNavigate } from "react-router-dom";

import { SceneCard, LoadingSceneCard, Upload } from "./components";
import { FnToggleScenesView, ScenesView } from "./types";

import { Error, Loading, Map, MapGrid, MapScenesLegend, DownloadDetections, DatePicker } from "../../components";
import { MAX_DATE, MIN_DATE } from "../../settings";
import { SceneFeature } from "../../types/scenes";
import { groupByScene, inferenceStatsPerGroup, SpeciesValidatedCount } from "../../utils/inferenceStats";
import { useInferences, useQueryParams, useScenes } from "../../hooks";
import { PANEL_SIZE } from "../../theme";

type SortOptions = "date" | "registered" | "count" | "validated";
type SortOrderOptions = "asc" | "desc";

interface SceneWithStats {
  originalScene: Feature<Geometry, SceneFeature>; // The original scene feature
  count: number;
  validated: number; // Assuming validated is a numeric value (e.g., count of validations)
  species: Record<string, number>; // Or the actual type of species from inferenceStats
  speciesValidated: SpeciesValidatedCount;
}

function Scenes() {
  const navigate = useNavigate();
  const mapRef = useRef<MapRef>(null);
  const [view, setView] = useState<ScenesView>(ScenesView.Map);
  const [filterBbox, setFilterBbox] = useState<boolean>(false);
  const [hoveredSceneId, setHoveredSceneId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOptions>(
    localStorage.getItem("sortBy") as SortOptions || "date"
  );
  const [sortOrder, setSortOrder] = useState<SortOrderOptions>(
    localStorage.getItem("sortOrder") as SortOrderOptions || "desc"
  );
  const [hoveredId, setHoveredId] = useState<string | number | null>(null);
  const [cursor, setCursor] = useState<string>("auto");

  const {
    selectedDates,
    setSelectedDates,
    bbox,
    setBbox,
  } = useQueryParams();
  const {
    isLoading,
    error,
    scenes,
    refetch
  } = useScenes({selectedDates, bbox, subResource: "user"});
  const { inferences, isLoading: inferenceLoading } = useInferences({
    selectedDates: [MIN_DATE, MAX_DATE],
    bbox: undefined,
    validated: undefined,
    asPoints: true,
    subResource: "user"
  });

  const inferenceStats = useMemo(() => {
    if (!inferences?.features) return {};
    const inferencesByScene = groupByScene(inferences.features);
    return inferenceStatsPerGroup(inferencesByScene);
  }, [inferences]);

  const scenesWithAssociatedStats = useMemo<SceneWithStats[]>(() => {
    if (!scenes?.features) return [];
    return scenes.features.map((scene: Feature<Geometry, SceneFeature>) => {
      const sceneKey = scene.properties.s3_prefix;
      const stats = inferenceStats[sceneKey];
      return {
        originalScene: scene,
        count: stats ? stats.count : 0,
        validated: stats ? stats.validated : 0,
        species: stats ? stats.species : {},
        speciesValidated: stats ? stats.speciesValidated : {},
      };
    });
  }, [scenes, inferenceStats]);
  // Sort scenes based on the selected sortBy and sortOrder
  const scenesSorted = useMemo(() => {
    if (!scenesWithAssociatedStats.length) return [];

    // Create a copy before sorting to avoid mutating the memoized array
    const sortableArray = [...scenesWithAssociatedStats];

    return sortableArray.sort((a, b) => {
      let comparison = 0;
      const propsA = a.originalScene.properties;
      const propsB = b.originalScene.properties;

      switch (sortBy) {
        case "count":
          comparison = a.count - b.count;
          break;
        case "validated":
          comparison = a.validated - b.validated;
          break;
        case "date":
        case "registered": {
          // Ensure these properties exist on SceneFeature and are date strings
          const valueA_str = propsA[sortBy] as string;
          const valueB_str = propsB[sortBy] as string;
          const dateA = new Date(valueA_str).valueOf();
          const dateB = new Date(valueB_str).valueOf();
          // Handle potential NaN from invalid date strings
          comparison = (isNaN(dateA) ? 0 : dateA) - (isNaN(dateB) ? 0 : dateB);
          break;
        }
        default:
          // eslint-disable-next-line no-console
          console.warn(`Attempting to sort by unhandled property: ${sortBy}`);
          break;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [scenesWithAssociatedStats, sortBy, sortOrder]);

  const panelBg = useColorModeValue("base.50", "base.700");
  const checkboxBg = useColorModeValue("base.50", "base.500");
  const borderColor = useColorModeValue("base.100a", "base.500");

  const toggleScenesView: FnToggleScenesView = useCallback(() => {
    if (view === ScenesView.List) {
      setView(ScenesView.Map);
    } else {
      setView(ScenesView.List);
    }
  }, [view]);

  useEffect(() => {
    if (filterBbox) {
      setBbox(mapRef.current?.getBounds().toArray().flat() as BBox);
    } else {
      setBbox(undefined);
    }
  }, [filterBbox, setBbox]);

  const handleBboxChange = useCallback(() => {
    if (filterBbox) {
      setBbox(mapRef.current?.getBounds().toArray().flat() as BBox);
    }
  }, [filterBbox, setBbox]);

  const resetFilters = () => {
    setSelectedDates([MIN_DATE, MAX_DATE]);
    setFilterBbox(false);
    setBbox(undefined);
  };
  const defaultFilterState = filterBbox === false && JSON.stringify(selectedDates) == JSON.stringify([MIN_DATE, MAX_DATE]);

  const onMapHover = (event: MapLayerMouseEvent) => {
    const feature = event.features && event.features[0];

    if (feature?.id !== undefined) {
      // Clear previous hover
      if (hoveredId !== null) {
        mapRef.current?.setFeatureState(
          { source: "grid", id: hoveredId },
          { hover: false }
        );
      }

      // Set new hover
      setHoveredId(feature.id);
      mapRef.current?.setFeatureState(
        { source: "grid", id: feature.id },
        { hover: true }
      );
      setCursor(feature.properties?.matches === 1 ? "pointer" : "grab");
    } else {
      setCursor("grab");
    }
  };

  const onMapLeave = () => {
    if (hoveredId !== null) {
      mapRef.current?.setFeatureState(
        { source: "grid", id: hoveredId },
        { hover: false }
      );
      setHoveredId(null);
    }
    setCursor("grab");
  };

  const onMapClick = useCallback((event: MapLayerMouseEvent) => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const features = map.queryRenderedFeatures(event.point, {layers: ["grid-fill"]});

    if (features && features.length > 0) {
      const clickedCellData = features[0].properties;
      const sceneIDs: string[] = clickedCellData?.sceneIDs ? JSON.parse(clickedCellData.sceneIDs) : [];
      if (clickedCellData?.matches === 1) {
        navigate(`/scenes/${sceneIDs[0]}`);
        return;
      }
      if (clickedCellData?.matches > 0) {
        map.fitBounds(turfBbox(features[0]) as BBox as [number, number, number, number], { padding: 40, maxZoom: 12 });
      }
    }
  }, [navigate]);

  return (
    <Box
      position="relative"
      display="grid"
      gridTemplateColumns={view === ScenesView.Map ? ["1fr", null, `${PANEL_SIZE} 1fr`] : "1fr"}
      gridTemplateRows={view === ScenesView.Map ? ["3fr 1fr", null, "1fr"] : "1fr"}
      overflow="hidden"
      mx={view === ScenesView.Map ? 0 : [0, 0, null, 48]}
    >
      <Grid
        templateRows={view === ScenesView.List ? "max-content max-content max-content 1fr" : "max-content max-content auto 1fr max-content"}
        p="5"
        gap="2"
        overflow="auto"
        boxShadow={view === ScenesView.Map ? ["0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)", null, "md"] : "none"}
        gridRow={view === ScenesView.Map ? ["2", null, "initial"] : "initial"}
        zIndex="10"
        borderRight={view === ScenesView.Map ? "1px solid" : "none"}
        borderColor={borderColor}
      >
        <Box display="flex" gap="5" alignItems="center">
          <Heading size="md" as="h2" flex="1">
            My Scenes
          </Heading>
          <Button
            size="sm"
            rightIcon={<CollecticonMap />}
            variant="primary-outline"
            onClick={toggleScenesView}
          >
            {view === ScenesView.Map ? "Hide Map" : "Show Map"}
          </Button>
        </Box>
        <Upload onSubmit={refetch} onUploadComplete={refetch} />
        <Box display="flex" flexDir={[ "column", "row"]} justifyContent="space-between" gap="4">
          <Box flex="1" display="flex" gap="4" alignItems="center">
            <Menu closeOnSelect={false} placement="bottom-end">
              <MenuButton as={Button} colorScheme="primary" variant="primary-outline" rightIcon={<CollecticonSwapVertical />} flex={view === ScenesView.Map ? 1 : "initial"} css={{ "& > span" : { flex: "initial" }}}>
                Sort By
              </MenuButton>
              <MenuList fontSize="sm" zIndex="popover">
                <MenuOptionGroup
                  title="Order"
                  type="radio"
                  value={sortOrder}
                  onChange={(e) => {
                    setSortOrder(e as "asc" | "desc");
                    localStorage.setItem("sortOrder", e as SortOrderOptions);
                  }}
                  defaultValue="desc"
                >
                  <MenuItemOption value="asc">Ascending</MenuItemOption>
                  <MenuItemOption value="desc">Descending</MenuItemOption>
                </MenuOptionGroup>
                <MenuDivider />
                <MenuOptionGroup
                  title="Options"
                  type="radio"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e as SortOptions);
                    localStorage.setItem("sortBy", e as SortOptions);
                  }}
                  defaultValue="date"
                >
                  <MenuItemOption value="date">Scene Date</MenuItemOption>
                  <MenuItemOption value="registered">Upload Date</MenuItemOption>
                  <MenuItemOption value="count">Detection Count</MenuItemOption>
                  <MenuItemOption value="validated">Validated</MenuItemOption>
                </MenuOptionGroup>
              </MenuList>
            </Menu>
            {view === ScenesView.List && (
              <>
                <Popover placement="bottom-start">
                  {({ isOpen }) => (
                    <>
                      <PopoverTrigger>
                        <Button
                          variant="primary-outline"
                          rightIcon={isOpen ? <CollecticonChevronUpSmall /> : <CollecticonChevronDownSmall />}
                        >
                      Filter by date range
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent py={1} px={1} minWidth="max-content">
                        <PopoverArrow />
                        <PopoverBody>
                          <DatePicker
                            minDate={MIN_DATE}
                            maxDate={MAX_DATE}
                            selectedDates={selectedDates}
                            setSelectedDates={setSelectedDates}
                          />
                        </PopoverBody>
                      </PopoverContent>
                    </>
                  )}
                </Popover>
                {(MIN_DATE.valueOf() < selectedDates[0].valueOf() ||  (MAX_DATE.valueOf() - 50000)  > selectedDates[1].valueOf()) && // use a 50 second buffer between repeated Date.now() calls for maxDate
                <Tag colorScheme="primary" overflow="hidden">
                  <TagLabel>{selectedDates[0].toLocaleDateString("en-US")} - {selectedDates[1].toLocaleDateString("en-US")}</TagLabel>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    ml={1}
                    mr={-2}
                    borderRadius="none"
                    icon={<CollecticonArrowSemiSpinCcw size="10px" />}
                    onClick={() => setSelectedDates([MIN_DATE, MAX_DATE])}
                    aria-label="Reset date filter"
                    title="Reset date filter"
                  />
                </Tag>}
              </>
            )}
          </Box>
          {view !== ScenesView.Map &&
          <DownloadDetections
            inferences={inferences}
            fileName={`all-detections-${new Date().toISOString().split(".")[0]}.csv`}
            isLoading={inferenceLoading}
            isDisabled={!inferences?.features.length}
          >
            Download All Inferences
          </DownloadDetections>}
        </Box>
        { isLoading || error ? (
          <>
            { isLoading &&
            <Box
              display="grid"
              gap="5"
              gridTemplateColumns="repeat(auto-fill, minmax(22rem,1fr))"
              gridAutoRows="max-content"
              mt="2"
            >
              <Loading opacity="0.75" fontSize="sm">Fetching uploaded scenes</Loading>
              { Array.from(Array(5)).map((a, ind) =>
              // eslint-disable-next-line react/no-array-index-key
                <LoadingSceneCard key={ind} />
              )}
            </Box>}
            { error && <Error mt="4">{ error.message }</Error> }
          </>
        ) : (
          <>
            {scenes?.features.length === 0 ? (
              <Box mt="2" display="flex" gap="2" flexDir="column" alignItems="flex-start">
                {defaultFilterState ?
                  <>
                    <Heading as="h4" size="sm">No scenes available</Heading>
                    <Text fontSize="sm">No scenes have been uploaded or processed. Click the &quot;Upload new scene&quot; button to get started.</Text>
                  </>
                  :
                  <>
                    <Heading as="h4" size="sm">No scenes match the current filters</Heading>
                    <Text fontSize="sm">Try changing or removing some of your filters, or adjusting the search area.</Text>
                    <Button
                      size="sm"
                      variant="base-soft-outline"
                      onClick={resetFilters}
                      leftIcon={<CollecticonArrowSemiSpinCcw />}
                    >
                    Reset Filters
                    </Button>
                  </>}
              </Box>
            ) : (
              <Box
                display="grid"
                gap="5"
                gridTemplateColumns="repeat(auto-fill, minmax(22rem,1fr))"
                gridAutoRows="max-content"
                mt="2"
                mb="12"
              >
                {scenesSorted.map((item: SceneWithStats, index: number) => {
                  return (
                    <SceneCard
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${item.originalScene.properties.s3_prefix}-${index}`}
                      scene={item.originalScene}
                      inferencesLoading={inferenceLoading}
                      species={item.species}
                      speciesValidated={item.speciesValidated}
                      setHoveredSceneId={setHoveredSceneId}
                      refetchScene={refetch}
                    />
                  );
                })}
              </Box>
            )}
          </>
        )}
        {view === ScenesView.Map &&
          <Box
            display="flex"
            bg={panelBg}
            width="100%"
            position="sticky"
            mt="-3.25rem"
            mb="-1.25rem"
            py={5}
            bottom={-5}
            left={0}
            right={0}
            flexDirection="column"
            zIndex="4"
          >
            <DownloadDetections
              inferences={inferences}
              fileName={`all-detections-${new Date().toISOString().split(".")[0]}.csv`}
              isLoading={inferenceLoading}
              isDisabled={!inferences?.features.length}
            >
              Download My Scene Inferences
            </DownloadDetections>
          </Box>}
      </Grid>
      {view === ScenesView.Map && (
        <Box display="grid" gridTemplateRows="1fr max-content">
          <Box position="relative">
            <Box
              position="absolute"
              top="4"
              left="4"
              zIndex="1000"
              borderRadius="md"
              padding="2"
              boxShadow="base"
              bgColor={checkboxBg}
              border="1px solid"
              borderColor="base.100a"
            >
              <Checkbox
                colorScheme="primary"
                isChecked={filterBbox}
                onChange={() => setFilterBbox((prev) => !prev)}
              >
                Filter scenes by the map
              </Checkbox>
            </Box>
            <Map
              ref={mapRef}
              onDragEnd={handleBboxChange}
              onZoomEnd={handleBboxChange}
              onMouseMove={onMapHover}
              onMouseLeave={onMapLeave}
              interactiveLayerIds={["grid-fill"]}
              onClick={onMapClick}
              cursor={cursor}
              basemapSwitcher
            >
              <MapGrid data={scenes} hoveredSceneId={hoveredSceneId} showGridLabels />
              <MapScenesLegend />
            </Map>
          </Box>
          <Box py="8" px="8" pt="4">
            <DatePicker
              minDate={MIN_DATE}
              maxDate={MAX_DATE}
              selectedDates={selectedDates}
              setSelectedDates={setSelectedDates}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default Scenes;
